import { CustomButton } from '@/shared/ui/components';
import { Input, Modal } from 'antd';
import { t } from 'ttag';
import ExclamationCircleIcon from './assets/exclamation-circle-icon.svg';
import { useImportStudyModal } from './useImportStudyModal';
import './importStudyModal.scss';

export interface ImportStudyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onImported: () => void;
}

const FIELD_ID = 'import-study-external-id';
const ERROR_ID = 'import-study-external-id-error';

/**
 * Asks for the CardioOne study ID of a study created in the external portal and imports it. The dialog is named `Import Study` by its antd title.
 *
 * @param isOpen - Whether the dialog is shown.
 * @param onClose - Called when the dialog should close.
 * @param onImported - Called after a study was imported.
 * @returns The Import Study dialog.
 */
const ImportStudyModal = ({ isOpen, onClose, onImported }: ImportStudyModalProps) => {
  const {
    externalId,
    isImporting,
    isImportDisabled,
    errorMessage,
    onChangeExternalId,
    onClickImport,
  } = useImportStudyModal({ isOpen, onClose, onImported });

  return (
    <Modal
      width={400}
      open={isOpen}
      rootClassName="import-study-modal"
      footer={null}
      closable={false}
      maskClosable={false}
      destroyOnHidden
      title={t`Import Study`}
    >
      <div className="import-study-modal__body">
        <p className="import-study-modal__description">
          {t`Create the study in the external portal first, then enter its ID below to import it.`}
        </p>
        <label className="import-study-modal__label" htmlFor={FIELD_ID}>
          {t`CardioOne study ID`}
        </label>
        <Input
          id={FIELD_ID}
          value={externalId}
          placeholder={t`Enter CardioOne study ID`}
          status={errorMessage ? 'error' : undefined}
          aria-invalid={!!errorMessage}
          aria-describedby={errorMessage ? ERROR_ID : undefined}
          onChange={onChangeExternalId}
        />
        {errorMessage && (
          <div id={ERROR_ID} className="import-study-modal__error">
            <img src={ExclamationCircleIcon} alt="" />
            <span>{errorMessage}</span>
          </div>
        )}
        <div className="import-study-modal__action">
          <CustomButton
            disabled={isImporting}
            onClick={onClose}
            size="middle"
            type="outline"
            style="neutral"
          >
            {t`Cancel`}
          </CustomButton>
          <CustomButton
            disabled={isImportDisabled}
            loading={isImporting}
            onClick={onClickImport}
            size="middle"
          >
            {t`Import Study`}
          </CustomButton>
        </div>
      </div>
    </Modal>
  );
};

export default ImportStudyModal;
